import Button from '@oberoninternal/travelbase-ds/components/action/Button';
import Body from '@oberoninternal/travelbase-ds/components/primitive/Body';
import Title from '@oberoninternal/travelbase-ds/components/primitive/Title';
import useSesame from '@oberoninternal/travelbase-ds/hooks/useSesame';
import { useThemeContext } from '@oberoninternal/travelbase-ds/hooks/useThemeContext';
import { Box, Flex } from '@rebass/grid';
import { ApolloClient, gql, NetworkStatus, NormalizedCacheObject } from '@apollo/client';
import endOfMonth from 'date-fns/endOfMonth';
import endOfWeek from 'date-fns/endOfWeek';
import startOfMonth from 'date-fns/startOfMonth';
import { debounce } from 'debounce';
import isEqual from 'lodash.isequal';
import omit from 'lodash.omit';
import pick from '../../utils/pick';
import { GetServerSidePropsContext, GetStaticPropsContext } from 'next';
import { ParsedUrlQuery } from 'querystring';
import React, { useCallback, useMemo } from 'react';
import { useInView } from 'react-intersection-observer';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import Skeleton from 'react-loading-skeleton';
import styled from 'styled-components';
import { decodeQueryParams } from 'use-query-params';
import activitySearchParams, {
    activitySearchFilterParams,
    ActivitySearchParams,
} from '../../constants/activitySearchParams';
import { BrandConfig } from '../../constants/brandConfig';
import parseToDateString from '../../constants/parseToDateString';
import { addApolloState, initApolloClient } from '../../createApolloClient';
import { FilterCategory } from '../../entities/Filter';
import { Locale } from '../../entities/Locale';
import {
    ActivitySearchDocument,
    ActivitySearchParamsInput,
    ActivitySearchQuery,
    ActivitySearchQueryVariables,
    ActivitySearchOrderEnum,
    TargetAudienceEnum,
    useActivitySearchQuery,
    useAgendaFiltersQuery,
    useCompanySearchQuery,
} from '../../generated/graphql';
import composeFilterCategory from '../../hooks/search/useComposeFilterCategory';
import useEateriesQuery from '../../hooks/useEateriesQuery';
import useFiltersActive from '../../hooks/useFiltersActive';
import useQueryParams from '../../hooks/useQueryParams';
import cssCalcBetweenViewport from '../../utils/cssCalcBetweenViewport';
import getRelevantTime from '../../utils/getRelevantTime';
import ActivityHit from '../ActivityHit';
import ActivityTile from '../ActivityTile';
import AgendaHeader, { AgendaView, Header, weekOpts } from '../AgendaHeader';
import ContentWrapper from '../ContentWrapper';
import CompanyTile from '../designsystem/CompanyTile';
import WideGrid, { Cell } from '../designsystem/WideGrid';
import Divider from '../Divider';
import FilterList from '../FilterList';
import Formed from '../Formed';
import Page from '../Page';
import SearchBottomBar from '../SearchBottomBar';
import SearchFiltersModal from '../SearchFiltersModal';
import SearchFilterTags from '../SearchFilterTags';
import TileCarousel from '../TileCarousel';
import { useCompanyImageSizes } from './Companies';

import DatepickerField from '../DatepickerField';
import { useDeviceSize } from '@oberoninternal/travelbase-ds/context/devicesize';
import SearchbarDatepickerInputs from '../designsystem/datepicker/SearchbarDatepickerInputs';
import addDays from 'date-fns/addDays';
import { useTenantContext } from '../../context/TenantContext';
import FavoriteIconButton from '../FavoriteIconButton';
import useGetPageSlugByLocale from '../../hooks/useGetPageSlugByLocale';
import RatingFilter from '../RatingFilter';
import SearchOrderSelectInput from '../SearchOrderSelectInput';

export const agendaQuery = gql`
    query ActivitySearch(
        $params: ActivitySearchParamsInput!
        $limit: Int
        $offset: Int
        $order: ActivitySearchOrderEnum!
        $startDate: String!
        $endDate: String!
    ) {
        activities(params: $params, limit: $limit, offset: $offset, order: $order) {
            ... on ActivitySearchResult {
                totalHits
                hits {
                    ...ActivityHit
                }
                placeCounts {
                    value
                    hits
                }
                themeCounts {
                    value
                    hits
                }
                targetAudienceCount {
                    value
                    hits
                }
            }
        }
    }

    fragment ActivityHit on Activity {
        id
        name
        slug
        highlighted
        place
        videoUrl
        externalBookingUrl
        bookingType

        themes {
            slug
            name
        }
        activityReviewStats {
            count
            average
        }
        minCapacity
        maxCapacity
        timeslots(startDate: $startDate, endDate: $endDate) {
            ...ActivityPlannerTimeslot
        }
        themes {
            slug
        }
        mainImage {
            transform(config: HERO, format: JPEG) {
                ...ListImage
            }
        }
        webpMainImage: mainImage {
            transform(config: HERO, format: WEBP) {
                ...ListImage
            }
        }
        webpListImage: listImage {
            transform(config: HERO, format: WEBP) {
                ...ListImage
            }
        }
        listImage {
            transform(config: HERO, format: JPEG) {
                ...ListImage
            }
        }
    }

    query AgendaFilters {
        activityThemes {
            slug
            name
            position
        }
        places {
            id
            position
            name
        }
    }
`;

export interface BaseProps {
    bakedInParams?: ActivitySearchParams;
    startParams?: ActivitySearchParams;
}

interface Props extends BaseProps {
    amountPerPage: number;
    ssr?: boolean;
    skipHighlights?: boolean;
    skipCompanies?: boolean;
    skipEateries?: boolean;
    hideTitle?: boolean;
}

export interface ActivitySearchValues {
    themeSlugs: string[];
    placeId: string[];
    view: AgendaView;
    targetAudiences: TargetAudienceEnum[];
    startDate: string;
    endDate: string;
    rating: number | null | undefined;
    order: ActivitySearchOrderEnum;
}

const targetAudienceMessages = defineMessages<TargetAudienceEnum>({
    ALL_AGES: { defaultMessage: 'Alle leeftijden' },
    ADULTS: { defaultMessage: 'Volwassenen' },
    CHILDREN: { defaultMessage: 'Kinderen' },
    WHEELCHAIR_ACCESSIBLE: { defaultMessage: 'Rolstoeltoegankelijk' },
    PETS: { defaultMessage: 'Met huisdier' },
});

const useImageSizes = () => {
    const { mediaQueries } = useThemeContext();
    return `(min-width: ${mediaQueries.xxl}) 25vw, (min-width: ${mediaQueries.xl}) 25vw, (min-width: ${mediaQueries.s}) 25vw, 100vw`;
};

const determinePeriod = (date: string, view: AgendaView): [string, string] => {
    switch (view) {
        case 'month': {
            const startDate = startOfMonth(new Date(date));
            const endDate = endOfMonth(startDate);
            return [parseToDateString(startDate), parseToDateString(endDate)];
        }
        case 'week': {
            const startDate = new Date(date);
            const endDate = endOfWeek(startDate, weekOpts);
            return [parseToDateString(startDate), parseToDateString(endDate)];
        }
        default:
            return [date, date];
    }
};

const filterKeys: Array<keyof ActivitySearchValues> = ['themeSlugs', 'placeId', 'targetAudiences'];

// NOTE: these are hopefully temporary hardcoded
const eaterySubCategories = [
    'paviljoens',
    'cafes',
    'restaurants',
    'catering',
    'snackbar',
    'discotheken',
    'vis-cafetaria',
];

const Agenda: React.FC<React.PropsWithChildren<Props>> = ({
    amountPerPage,
    bakedInParams,
    startParams,
    skipHighlights = false,
    skipCompanies = false,
    skipEateries = false,
    ssr = false,
    hideTitle = false,
}) => {
    const { brandConfig } = useTenantContext();
    const intl = useIntl();
    const sizes = useImageSizes();
    const companySizes = useCompanyImageSizes();
    const deviceSize = useDeviceSize();
    const shouldAutoSave = deviceSize !== 'mobile';
    const { getPageSlugByLocale } = useGetPageSlugByLocale();
    const companyPageSlug = getPageSlugByLocale('COMPANY');
    const filterModal = useSesame();
    const today = useMemo(() => new Date(), []);
    const searchOrder = {
        popularity: ActivitySearchOrderEnum.Random,
        ratingDesc: ActivitySearchOrderEnum.ReviewRatingDesc,
    };

    /** parse params */
    const [params, setParams] = useQueryParams(activitySearchParams);
    const [filtersActive, removeActiveFilters] = useFiltersActive(activitySearchFilterParams);
    const {
        placeId,
        startDate = parseToDateString(today),
        endDate = parseToDateString(addDays(today, 7)),
        targetAudiences = [],
        themeSlugs = [],
        view = 'day',
        companyId,
        order,
        rating,
    } = {
        ...startParams,
        ...params,
        ...bakedInParams,
    };
    /** fetch filters */
    const filtersQuery = useAgendaFiltersQuery({ ssr });
    const onViewChange = useCallback(
        (newView: AgendaView) => {
            if (newView === 'day') {
                const [periodStartDate, periodEndDate] = determinePeriod(
                    parseToDateString(today),
                    newView as AgendaView
                );
                setParams({ startDate: periodStartDate, endDate: periodEndDate, view: newView });
            } else {
                const [periodStartDate, periodEndDate] = determinePeriod(startDate, newView as AgendaView);
                setParams({ startDate: periodStartDate, endDate: periodEndDate, view: newView });
            }
        },

        // eslint-disable-next-line react-hooks/exhaustive-deps
        [setParams]
    );
    const initialStartDate = startDate.length > 0 ? startDate : parseToDateString(today);
    const initialEndDate = endDate.length > 0 ? endDate : parseToDateString(addDays(today, 7));
    const initialOrder = order ?? searchOrder.popularity;
    const initialRating = rating;
    /** process current filters */
    const paramsInput: ActivitySearchParamsInput = {
        startDate: initialStartDate,
        endDate: initialEndDate,
        placeId,
        targetAudiences: targetAudiences as TargetAudienceEnum[],
        themeSlugs,
        companyId,
        rating,
    };

    /** fetch activities */
    const { data, loading, fetchMore, variables, networkStatus } = useActivitySearchQuery({
        variables: {
            limit: amountPerPage,
            order: initialOrder,
            params: paramsInput,
            startDate: initialStartDate,
            endDate: initialEndDate,
        },
        notifyOnNetworkStatusChange: true,
        ssr,
    });

    const highlightedQuery = useActivitySearchQuery({
        variables: {
            limit: 4,
            order: initialOrder,
            params: {
                ...paramsInput,
                highlighted: true,
            },
            startDate: initialStartDate,
            endDate: initialEndDate,
        },
        notifyOnNetworkStatusChange: true,
        ssr,
        skip: skipHighlights,
    });

    const canLoadMoreActivities = data && data.activities.totalHits > data.activities.hits.length;

    /** as eateries are below the fold, we'll query 'm lazily */
    const [eateriesRef, eateriesInView] = useInView({ triggerOnce: true, rootMargin: '500px' });
    const eateriesQuery = useEateriesQuery({
        variables: {
            limit: 8,
            params: {
                placeId,
                showInCalendar: true,
                startDate: initialStartDate,
                endDate: initialEndDate,
            },
        },
        skip: !eateriesInView || skipEateries,
    });
    const companiesQuery = useCompanySearchQuery({
        variables: {
            limit: 8,
            params: {
                placeId,
                showInCalendar: true,
                startDate: initialStartDate,
                endDate: initialEndDate,
                targetAudiences: targetAudiences as TargetAudienceEnum[],
                themeSlugs,
            },
        },
        ssr,
        skip: skipCompanies,
    });

    /** handle formik inputs */
    const handleSubmit = useCallback(
        (values: ActivitySearchValues) => {
            // by changing the params, apollo will refetch the query automagically
            setParams(
                {
                    startDate: values.startDate,
                    endDate: values.endDate,
                    order: values.order,
                    view: values.view,
                    placeId: values.placeId?.[0],
                    targetAudiences: values.targetAudiences,
                    themeSlugs: values.themeSlugs,
                    rating: values.rating,
                },
                'replaceIn'
            );
        },
        [setParams]
    );
    // TODO use useMemo in order te create the function
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const handleAutoSave = useCallback(debounce(handleSubmit), [handleSubmit]);

    /** parse activities results */
    const { totalHits = 0 } = data?.activities ?? {};

    const activities = data?.activities.hits;

    /** parse companies results */
    const filteredCompanies = useMemo(
        () =>
            companiesQuery.data?.companies.hits.filter(company =>
                company.categories.every(category => !eaterySubCategories.includes(category.slug))
            ),
        [companiesQuery.data]
    );

    const activityThemeCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'themeSlugs',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Thema' }),
                filterProperties: [...(filtersQuery.data?.activityThemes ?? [])].sort(
                    (activityThemeA, activityThemeB) => activityThemeA.position - activityThemeB.position
                ),
                filterResolver: ({ slug, name }) => ({ handle: slug, label: name ?? '' }),
                filterCounts: data?.activities.themeCounts ?? [],
                filterCountKey: 'value',
            }),
        [data, filtersQuery.data, intl]
    );
    const placesFilterCategory: FilterCategory = useMemo(
        () =>
            composeFilterCategory({
                categoryHandle: 'placeId',
                categoryLabel: intl.formatMessage({ defaultMessage: 'Plaats' }),
                filterProperties: filtersQuery.data?.places ?? [],
                filterResolver: ({ id, name }) => ({ handle: id, label: name }),
                filterCounts: data?.activities.placeCounts ?? [],
                filterCountKey: 'value',
            }),
        [data, filtersQuery.data, intl]
    );

    const targetAudienceCategory: FilterCategory = useMemo(() => {
        const targets = Object.entries(targetAudienceMessages).map(([key, value]) => ({
            label: intl.formatMessage(value),
            handle: key,
        }));
        return composeFilterCategory({
            categoryHandle: 'targetAudiences',
            categoryLabel: intl.formatMessage({ defaultMessage: 'Doelgroep' }),
            filterProperties: targets,
            filterCounts: data?.activities.targetAudienceCount ?? [],
            filterCountKey: 'value',
        });
    }, [data, intl]);

    const filterCategories = [activityThemeCategory, placesFilterCategory, targetAudienceCategory];

    /** filters changed */
    const activityFilters = useMemo(() => pick(params, Object.keys(activitySearchFilterParams)), [params]);
    const bakedActivityFilters = useMemo(
        () => pick(bakedInParams, Object.keys(activitySearchFilterParams)),
        [bakedInParams]
    );
    const filtersUnchanged = useMemo(
        () =>
            Object.values(activityFilters).length === 0 ||
            (Object.values(bakedActivityFilters).length > 0 && isEqual(activityFilters, bakedActivityFilters)),
        [activityFilters, bakedActivityFilters]
    );
    const resetDate = useCallback(() => {
        onViewChange('day');
    }, [onViewChange]);

    return (
        <Page id="search-page">
            <Formed<ActivitySearchValues>
                skipPrompt
                initialValues={{
                    startDate: initialStartDate,
                    endDate: initialEndDate,
                    view: view as AgendaView,
                    placeId: placeId ? [placeId] : [],
                    themeSlugs,
                    targetAudiences: targetAudiences as TargetAudienceEnum[],
                    rating: initialRating,
                    order: initialOrder,
                }}
                handleAutoSave={(values, helpers) => {
                    helpers.setSubmitting(true);
                    handleAutoSave(values);
                }}
                handleSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting }) => {
                    const isLoading = (loading && networkStatus !== NetworkStatus.fetchMore) || isSubmitting;
                    const isLoadingHighlighted =
                        highlightedQuery.networkStatus === NetworkStatus.loading || isSubmitting;
                    return (
                        <StyledContentWrapper>
                            <AgendaHeader onViewChange={onViewChange} resetDate={resetDate} />
                            <Flex flexDirection={['column', null, null, 'row']}>
                                <LeftBar className="gt-m">
                                    <SectionHeader hideTitle={hideTitle}>
                                        <Title style={{ fontWeight: 600 }}>
                                            {totalHits === 1 ? (
                                                <>
                                                    <span>{totalHits}</span>{' '}
                                                    <FormattedMessage defaultMessage="activiteit" />
                                                </>
                                            ) : (
                                                <>
                                                    <span>{totalHits}</span>{' '}
                                                    <FormattedMessage defaultMessage="activiteiten" />
                                                </>
                                            )}
                                        </Title>
                                    </SectionHeader>
                                    <FilterContainer className="gt-m">
                                        <DatepickerField
                                            optionalSingleDate
                                            startName="startDate"
                                            endName="endDate"
                                            align="left"
                                        >
                                            <SearchbarDatepickerInputs
                                                startLabel={<FormattedMessage defaultMessage="Van" />}
                                                endLabel={<FormattedMessage defaultMessage="Tot" />}
                                                withVerticalPadding
                                                rounded
                                            />
                                        </DatepickerField>
                                    </FilterContainer>
                                    <Box my={4}>
                                        <Button variant="outline" size="small" onClick={() => resetDate()}>
                                            <FormattedMessage defaultMessage="Reset" />
                                        </Button>
                                    </Box>
                                    <Divider />
                                    {filtersQuery.loading && <FilterList mt={6} loading />}
                                    {!filtersQuery.loading && (
                                        <>
                                            <SearchFilterTags
                                                shouldAutoSave={shouldAutoSave}
                                                categories={filterCategories}
                                            />
                                            <FilterList categories={filterCategories} />
                                            <RatingFilter />
                                        </>
                                    )}
                                </LeftBar>

                                <SearchBottomBar
                                    allowedActions={['filters']}
                                    filtersActive={filtersActive}
                                    onFiltersClick={filterModal.onOpen}
                                />
                                <SearchFiltersModal
                                    dirtyCheckKeys={filterKeys}
                                    filtersActive={filtersActive}
                                    onClose={filterModal.onClose}
                                    open={filterModal.open}
                                    removeActiveFilters={removeActiveFilters}
                                >
                                    <FilterList categories={filterCategories} />
                                </SearchFiltersModal>

                                <ListingSection>
                                    {!isLoading && totalHits === 0 && (
                                        <section>
                                            <SectionHeader>
                                                <Title>
                                                    <FormattedMessage defaultMessage="Geen activiteiten gevonden" />
                                                </Title>
                                            </SectionHeader>
                                            <Body>
                                                <FormattedMessage defaultMessage="Op deze dag zijn er geen activiteiten met deze filters." />
                                            </Body>
                                            {!filtersUnchanged && (
                                                <>
                                                    <br />
                                                    <Button
                                                        style={{
                                                            alignSelf: 'flex-start',
                                                        }}
                                                        variant="outline"
                                                        onClick={removeActiveFilters}
                                                        type="button"
                                                    >
                                                        <FormattedMessage defaultMessage="Filters verwijderen" />
                                                    </Button>
                                                </>
                                            )}
                                        </section>
                                    )}
                                    {isLoadingHighlighted && (
                                        <section>
                                            <SectionHeader>
                                                <Skeleton width="20rem" height="2rem" />
                                            </SectionHeader>
                                            <WideGrid widths={[100, 50, 50]}>
                                                {[...new Array(2)].map((_, i) => (
                                                    <Cell key={i}>
                                                        <ActivityTile loading large />
                                                    </Cell>
                                                ))}
                                            </WideGrid>
                                        </section>
                                    )}
                                    {!isLoadingHighlighted &&
                                        highlightedQuery.data?.activities.hits &&
                                        highlightedQuery.data?.activities.hits.length > 0 && (
                                            <section>
                                                <SectionHeader>
                                                    <SearchOrderSelectInput searchOrder={searchOrder} />
                                                </SectionHeader>

                                                <TileCarousel
                                                    large
                                                    tiles={highlightedQuery.data?.activities.hits?.map((a, i) => (
                                                        <ActivityHit
                                                            key={i}
                                                            hit={a}
                                                            dontPassDate={view !== 'day'}
                                                            sizes={sizes}
                                                            large
                                                            cta={brandConfig.ctaOnActivityTile}
                                                        />
                                                    ))}
                                                    canLoadMore={
                                                        highlightedQuery.data.activities.hits.length <
                                                        highlightedQuery.data.activities.totalHits
                                                    }
                                                    onLoadMore={() =>
                                                        highlightedQuery.fetchMore({
                                                            variables: {
                                                                offset: highlightedQuery.data?.activities.hits.length,
                                                            },
                                                            updateQuery: (prev, next) => ({
                                                                ...prev,
                                                                activities: {
                                                                    ...prev.activities,
                                                                    hits: prev.activities.hits.concat(
                                                                        next.fetchMoreResult?.activities.hits ?? []
                                                                    ),
                                                                },
                                                            }),
                                                        })
                                                    }
                                                    loadingIndicator={<ActivityTile large loading />}
                                                />
                                            </section>
                                        )}
                                    {isLoading && (
                                        <section>
                                            <SectionHeader>
                                                <Skeleton width="20rem" height="2rem" />
                                            </SectionHeader>
                                            <WideGrid widths={[100, 50, 100 / 3]}>
                                                {[...new Array(6)].map((x, i) => (
                                                    <Cell key={i}>
                                                        <ActivityTile loading />
                                                    </Cell>
                                                ))}
                                            </WideGrid>
                                        </section>
                                    )}
                                    {activities && activities.length > 0 && (
                                        <section>
                                            {!isLoadingHighlighted &&
                                            highlightedQuery.data?.activities.hits &&
                                            highlightedQuery.data?.activities.hits.length > 0 ? (
                                                <SectionHeader>
                                                    <Title>
                                                        <FormattedMessage defaultMessage="Activiteiten" />
                                                    </Title>
                                                </SectionHeader>
                                            ) : (
                                                <SectionHeader>
                                                    <SearchOrderSelectInput searchOrder={searchOrder} />
                                                </SectionHeader>
                                            )}
                                            <WideGrid widths={[100, 50, 100 / 3]}>
                                                {activities.map((a, i) => (
                                                    <Cell key={i}>
                                                        <ActivityHit
                                                            hit={a}
                                                            dontPassDate={view !== 'day'}
                                                            sizes={sizes}
                                                        />
                                                        {i !== activities.length - 1 && (
                                                            <Divider mt="2.4rem" mb={0} className="lt-s" />
                                                        )}
                                                    </Cell>
                                                ))}
                                            </WideGrid>

                                            {canLoadMoreActivities && (
                                                <Flex width={1} justifyContent="center" my={7}>
                                                    <Button
                                                        submitting={networkStatus === NetworkStatus.fetchMore}
                                                        variant="outline"
                                                        onClick={() =>
                                                            fetchMore({
                                                                variables: {
                                                                    ...variables,
                                                                    offset: data?.activities.hits.length ?? 0,
                                                                },
                                                                updateQuery: (prev, { fetchMoreResult }) => {
                                                                    if (
                                                                        !prev.activities ||
                                                                        !fetchMoreResult?.activities
                                                                    ) {
                                                                        return prev;
                                                                    }

                                                                    return {
                                                                        ...prev,
                                                                        activities: {
                                                                            ...prev.activities,
                                                                            hits: [
                                                                                ...prev.activities.hits,
                                                                                ...fetchMoreResult.activities.hits,
                                                                            ],
                                                                        },
                                                                    };
                                                                },
                                                            })
                                                        }
                                                    >
                                                        <FormattedMessage defaultMessage="Meer laden..." />
                                                    </Button>
                                                </Flex>
                                            )}
                                        </section>
                                    )}
                                    <section ref={eateriesRef}>
                                        {eateriesQuery.networkStatus === NetworkStatus.loading && (
                                            <>
                                                <SectionHeader>
                                                    <Skeleton width="20rem" height="2rem" />
                                                </SectionHeader>
                                                <TileCarousel
                                                    tiles={[...new Array(3)].map((_, i) => (
                                                        <ActivityTile loading key={i} />
                                                    ))}
                                                />
                                            </>
                                        )}
                                        {eateriesQuery.data?.companies &&
                                            eateriesQuery.data.companies.hits.length > 0 && (
                                                <>
                                                    {!hideTitle && (
                                                        <SectionHeader>
                                                            <Title>
                                                                <FormattedMessage defaultMessage="Eten en drinken" />
                                                            </Title>
                                                        </SectionHeader>
                                                    )}
                                                    <TileCarousel
                                                        canLoadMore={
                                                            eateriesQuery.data.companies.hits.length <
                                                            eateriesQuery.data.companies.totalHits
                                                        }
                                                        onLoadMore={() =>
                                                            eateriesQuery.fetchMore({
                                                                variables: {
                                                                    offset: eateriesQuery.data?.companies.hits.length,
                                                                },
                                                                updateQuery: (prev, next) => ({
                                                                    ...prev,
                                                                    companies: {
                                                                        ...prev.companies,
                                                                        hits: prev.companies.hits.concat(
                                                                            next.fetchMoreResult?.companies.hits ?? []
                                                                        ),
                                                                    },
                                                                }),
                                                            })
                                                        }
                                                        loadingIndicator={<CompanyTile loading />}
                                                        tiles={eateriesQuery.data.companies.hits.map((company, i) => (
                                                            <CompanyTile
                                                                key={i}
                                                                name={company.name}
                                                                place={company.place}
                                                                icon={
                                                                    brandConfig.enableFavorites && (
                                                                        <FavoriteIconButton
                                                                            type="company"
                                                                            id={company.id}
                                                                        />
                                                                    )
                                                                }
                                                                time={
                                                                    view === 'day' || startDate === endDate
                                                                        ? getRelevantTime(
                                                                              intl,
                                                                              company.normalOperatingHours,
                                                                              company.specialOperatingHours,
                                                                              new Date(startDate)
                                                                          )
                                                                        : undefined
                                                                }
                                                                linkProps={{
                                                                    href: {
                                                                        pathname: `/${companyPageSlug}/${company.slug}`,
                                                                    },
                                                                    locale: intl.locale,
                                                                }}
                                                                sources={[
                                                                    {
                                                                        srcSet:
                                                                            company.webpListImage?.transform?.srcSet ??
                                                                            company.webpMainImage?.transform?.srcSet,
                                                                        type: 'image/webp',
                                                                        sizes: companySizes,
                                                                    },
                                                                    {
                                                                        srcSet:
                                                                            company.listImage?.transform?.srcSet ??
                                                                            company.mainImage?.transform?.srcSet,
                                                                        type: 'image/jpeg',
                                                                        sizes: companySizes,
                                                                    },
                                                                ]}
                                                                fallback={{
                                                                    src:
                                                                        company.listImage?.transform?.src ??
                                                                        company.mainImage?.transform?.src,
                                                                }}
                                                            />
                                                        ))}
                                                    />
                                                </>
                                            )}
                                    </section>
                                    {companiesQuery.data && filteredCompanies && filteredCompanies.length > 0 && (
                                        <section>
                                            {!hideTitle && (
                                                <SectionHeader>
                                                    <Title>
                                                        <FormattedMessage defaultMessage="Breng een bezoek aan" />
                                                    </Title>
                                                </SectionHeader>
                                            )}
                                            <TileCarousel
                                                tiles={filteredCompanies.map((company, i) => (
                                                    <CompanyTile
                                                        key={i}
                                                        name={company.name}
                                                        place={company.place}
                                                        videoUrl={company.videoUrl}
                                                        time={
                                                            view === 'day'
                                                                ? getRelevantTime(
                                                                      intl,
                                                                      company.normalOperatingHours,
                                                                      company.specialOperatingHours,
                                                                      new Date(startDate)
                                                                  )
                                                                : undefined
                                                        }
                                                        icon={
                                                            brandConfig.enableFavorites && (
                                                                <FavoriteIconButton type="company" id={company.id} />
                                                            )
                                                        }
                                                        linkProps={{
                                                            href: {
                                                                pathname: `/${companyPageSlug}/${company.slug}`,
                                                            },
                                                            locale: intl.locale,
                                                        }}
                                                        sources={[
                                                            {
                                                                srcSet:
                                                                    company.webpListImage?.transform?.srcSet ??
                                                                    company.webpMainImage?.transform?.srcSet,
                                                                type: 'image/webp',
                                                                sizes: companySizes,
                                                            },
                                                            {
                                                                srcSet:
                                                                    company.listImage?.transform?.srcSet ??
                                                                    company.mainImage?.transform?.srcSet,
                                                                type: 'image/jpeg',
                                                                sizes: companySizes,
                                                            },
                                                        ]}
                                                        fallback={{
                                                            src:
                                                                company.listImage?.transform?.src ??
                                                                company.mainImage?.transform?.src,
                                                        }}
                                                    />
                                                ))}
                                                canLoadMore={
                                                    companiesQuery.data.companies.hits.length <
                                                    companiesQuery.data.companies.totalHits
                                                }
                                                onLoadMore={() =>
                                                    companiesQuery.fetchMore({
                                                        variables: {
                                                            offset: companiesQuery.data?.companies.hits.length,
                                                        },
                                                        updateQuery: (prev, next) => ({
                                                            ...prev,
                                                            companies: {
                                                                ...prev.companies,
                                                                hits: prev.companies.hits.concat(
                                                                    next.fetchMoreResult?.companies.hits ?? []
                                                                ),
                                                            },
                                                        }),
                                                    })
                                                }
                                                loadingIndicator={<CompanyTile loading />}
                                            />
                                        </section>
                                    )}
                                </ListingSection>
                            </Flex>
                        </StyledContentWrapper>
                    );
                }}
            </Formed>
        </Page>
    );
};

export default Agenda;

interface Options {
    amountsPerPage?: number;
    pathname?: string;
    startParams?: ActivitySearchParams;
    bakedInParams?: ActivitySearchParams;
    client?: ApolloClient<NormalizedCacheObject>;
    brandConfig: BrandConfig;
}

export const createGetServerSideProps =
    <Q extends ParsedUrlQuery>({
        amountsPerPage,
        startParams = {},
        bakedInParams = {},
        client: existingClient,
    }: Options) =>
    async (context: GetStaticPropsContext<Q> | GetServerSidePropsContext<Q>) => {
        const query: ParsedUrlQuery | undefined = 'query' in context ? context.query : context.params;
        const today = new Date();

        const queryParams = {
            ...startParams,
            ...decodeQueryParams(activitySearchParams, omit(query, ['locale', ...Object.keys(bakedInParams)])),
            ...bakedInParams,
        };

        const {
            startDate = parseToDateString(today),
            endDate = parseToDateString(today),
            targetAudiences,
            themeSlugs,
            placeId,
            rating,
            order,
        } = queryParams;

        const paramsInput: ActivitySearchParamsInput = {
            startDate,
            endDate,
            targetAudiences: targetAudiences as TargetAudienceEnum[] | undefined,
            themeSlugs,
            placeId,
            rating,
        };

        const client = existingClient ?? initApolloClient(context.locale as Locale);

        await client.query<ActivitySearchQuery, ActivitySearchQueryVariables>({
            query: ActivitySearchDocument,
            variables: {
                // some protection as an offset < 0 will let the BE explode 🧨
                offset: 0,
                order: order ?? ActivitySearchOrderEnum.Random,
                limit: amountsPerPage,
                params: paramsInput,
                startDate,
                endDate,
            },
        });

        return addApolloState(client, { props: { bakedInParams, startParams } });
    };

const StyledContentWrapper = styled(ContentWrapper)`
    padding-bottom: 8rem;
`;

const SectionHeader = styled(Header)<{ hideTitle?: boolean }>`
    margin: ${({ hideTitle }) => (hideTitle ? '0 0 4rem 0' : '4rem 0')};

    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.xs}) {
        margin: ${({ hideTitle }) => (hideTitle ? '0 0 1.6rem' : '4rem 0 1.6rem')};
    }
`;

const LeftBar = styled(Box)`
    flex-shrink: 0;
    @media screen and (min-width: ${({ theme }) => theme.mediaQueries.s}) {
        width: 32.8rem;
        padding-right: ${({ theme }) => theme.spacing['60_Large']};

        ${SectionHeader} {
            margin-bottom: 2.4rem;
        }
    }
`;

const FilterContainer = styled.div`
    display: flex;
    flex-flow: column;

    > span + span {
        margin-top: 1rem;
    }
`;

const ListingSection = styled.section`
    width: 100%;

    & > * + * {
        margin-top: ${cssCalcBetweenViewport(24, 56)};
    }
`;
